import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Empty, Input, Select, Space, Table, Tag } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { ProjectSelector } from '@/components/project/ProjectSelector';
import { useHireMasterLabels } from '@/hooks/useHireMasterLabels';
import pmvHireService, { type PmvHireAgreement } from '@/services/pmv-hire.service';
import {
  HIRE_COMMERCIAL_STATUSES,
  HIRE_STATUS_COLOR,
  hireTableCellEllipsis,
  hireTableLinkBtnStyle,
  resolveLabel,
} from '@/utils/pmvHireUi';

/** Fleet-style on-hire register — enterprise labels, filters, no raw UUIDs. */
const PmvHireRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState<string | undefined>(params.get('status') || 'ON_HIRE');
  const [q, setQ] = useState('');
  const [supplierId, setSupplierId] = useState<string | undefined>();
  const [projectId, setProjectId] = useState<string | undefined>();

  const {
    supplierLabelById,
    equipmentLabelById,
    projectLabelById,
    poLabelById,
    supplierOptions,
    setSupplierSearch,
  } = useHireMasterLabels({ enablePos: true });

  const listQ = useQuery({
    queryKey: ['pmv-hire-register', status, q, supplierId, projectId],
    queryFn: () =>
      pmvHireService.listAgreements({
        status,
        q: q || undefined,
        supplier_id: supplierId,
        project_id: projectId,
        limit: 200,
      }),
  });

  const items = listQ.data?.items ?? [];
  const empty = !listQ.isLoading && items.length === 0;

  const columns = useMemo(
    () => [
      {
        title: 'Agreement',
        dataIndex: 'agreement_number',
        width: 180,
        fixed: 'left' as const,
        ellipsis: true,
        render: (v: string, r: PmvHireAgreement) => (
          <div style={hireTableCellEllipsis}>
            <Button
              type="link"
              style={hireTableLinkBtnStyle}
              onClick={() => navigate(`/asset/pmv/hire/agreements/${r.id}`)}
            >
              {v}
            </Button>
          </div>
        ),
      },
      {
        title: 'Equipment',
        dataIndex: 'pmv_profile_id',
        width: 220,
        ellipsis: true,
        render: (id: string) => (
          <div style={hireTableCellEllipsis}>
            <Button
              type="link"
              style={hireTableLinkBtnStyle}
              onClick={() => navigate(`/asset/pmv/${id}?tab=hire`)}
            >
              {resolveLabel(equipmentLabelById, id, 'Passport')}
            </Button>
          </div>
        ),
      },
      {
        title: 'Ownership',
        dataIndex: 'ownership_type',
        width: 130,
        render: (v: string) => (
          <Tag>{v === 'RENTED' || v === 'LEASED' ? `Hired (${v})` : v}</Tag>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'commercial_status',
        width: 160,
        render: (s: string) => <Tag color={HIRE_STATUS_COLOR[s] || 'default'}>{s}</Tag>,
      },
      {
        title: 'Supplier',
        dataIndex: 'supplier_id',
        width: 200,
        ellipsis: true,
        render: (id: string) => (
          <div style={hireTableCellEllipsis}>{resolveLabel(supplierLabelById, id)}</div>
        ),
      },
      {
        title: 'Project',
        dataIndex: 'project_id',
        width: 180,
        ellipsis: true,
        render: (id: string | null) => (
          <div style={hireTableCellEllipsis}>{resolveLabel(projectLabelById, id)}</div>
        ),
      },
      {
        title: 'Site',
        dataIndex: 'site_reference',
        width: 120,
        ellipsis: true,
        render: (v: string | null) => v || '—',
      },
      {
        title: 'PO',
        dataIndex: 'purchase_order_id',
        width: 140,
        ellipsis: true,
        render: (id: string | null) => (id ? resolveLabel(poLabelById, id, id.slice(0, 8)) : '—'),
      },
      {
        title: 'Contract ref',
        dataIndex: 'contract_reference',
        width: 120,
        ellipsis: true,
        render: (v: string | null) => v || '—',
      },
      { title: 'Currency', dataIndex: 'currency', width: 80 },
      {
        title: 'On-hire',
        dataIndex: 'commercial_on_hire_at',
        width: 150,
        ellipsis: true,
        render: (v: string | null) => (v ? String(v).slice(0, 16) : '—'),
      },
    ],
    [equipmentLabelById, navigate, poLabelById, projectLabelById, supplierLabelById]
  );

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="On-Hire Equipment"
        subtitle="Live hired/rented fleet register — PMV identity; Procurement Vendor & PO remain authorities"
        primaryActions={
          <Space wrap>
            <Button type="primary" onClick={() => navigate('/asset/pmv/hire/agreements/new')}>
              New agreement
            </Button>
            <Button onClick={() => navigate('/asset/pmv/hire/agreements')}>Agreements</Button>
            <Button onClick={() => navigate('/asset/pmv/hire/control')}>Hire Control</Button>
            <Button onClick={() => navigate('/asset/pmv/hire/on-off')}>On / Off-Hire</Button>
          </Space>
        }
        extra={
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="Search agreement / site"
              style={{ width: 200 }}
              onSearch={setQ}
            />
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Supplier"
              style={{ width: 220 }}
              value={supplierId}
              onChange={setSupplierId}
              onSearch={setSupplierSearch}
              filterOption={false}
              options={supplierOptions}
            />
            <div style={{ minWidth: 200 }}>
              <ProjectSelector
                value={projectId}
                onChange={(v) => setProjectId(v || undefined)}
                allowClear
                placeholder="Project"
              />
            </div>
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 180 }}
              value={status}
              onChange={(v) => {
                setStatus(v);
                const next = new URLSearchParams(params);
                if (v) next.set('status', v);
                else next.delete('status');
                navigate({ search: next.toString() });
              }}
              options={HIRE_COMMERCIAL_STATUSES.map((v) => ({ value: v, label: v }))}
            />
          </Space>
        }
      />
      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        message="No parallel RentalEquipmentMaster. Equipment = PMV profile; vendor = Procurement supplier; commercial terms = hire agreement."
      />
      {empty ? (
        <Empty
          description="No agreements match these filters"
          style={{ marginTop: 48 }}
        >
          <Button type="primary" onClick={() => navigate('/asset/pmv/hire/agreements/new')}>
            Create hire agreement
          </Button>
        </Empty>
      ) : (
        <Table
          rowKey="id"
          size="small"
          loading={listQ.isLoading}
          dataSource={items}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          scroll={{ x: 1680 }}
          tableLayout="fixed"
          columns={columns}
        />
      )}
    </WorkspaceLayout>
  );
};

export default PmvHireRegisterPage;
